import { useCallback, useState } from "react";

/** Default rows shown per page in an expanded section. */
const SECTION_PAGE = 5;

/** One expanded section's current page: a cursor window plus its size. */
export interface SectionPage {
  pageSize: number;
  first: number | null;
  last: number | null;
  after: string | null;
  before: string | null;
  pageIndex: number;
}

/** The first page for a freshly expanded section, at the given size. */
export const firstPage = (pageSize = SECTION_PAGE): SectionPage => ({
  pageSize,
  first: pageSize,
  last: null,
  after: null,
  before: null,
  pageIndex: 0,
});

export interface SectionPagination<TData> {
  /** Ids of the open sections. */
  expanded: ReadonlySet<string>;
  /** The current page window per open section. */
  pageById: Record<string, SectionPage>;
  /** The loaded data per section that has reported a page. */
  dataById: Record<string, TData>;
  /** Sections whose next page is in flight. */
  loadingById: Record<string, boolean>;
  /** Opens/closes a section, seeding its first page on open. */
  toggle: (id: string) => void;
  /** Advances a section to the next page, from its end cursor. */
  goToNextPage: (id: string, endCursor: string | null) => void;
  /** Steps a section back a page, from its start cursor. */
  goToPrevPage: (id: string, startCursor: string | null) => void;
  /** Resets a section to its first page at a new size. */
  setPageSize: (id: string, size: number) => void;
  /** Records a section's loaded page and clears its loading flag. */
  onLoaded: (id: string, data: TData) => void;
}

/**
 * Per-section cursor pagination for an accordion grid: which sections are open,
 * each one's current page window, its loaded data, and whether a page is in
 * flight. The caller fetches each open section's page (from `pageById`) and
 * reports it back via `onLoaded`. Generic over the section's data shape.
 */
export function useSectionPagination<TData>(): SectionPagination<TData> {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [pageById, setPageById] = useState<Record<string, SectionPage>>({});
  const [dataById, setDataById] = useState<Record<string, TData>>({});
  const [loadingById, setLoadingById] = useState<Record<string, boolean>>({});

  const startLoading = useCallback((id: string) => {
    setLoadingById((l) => (l[id] ? l : { ...l, [id]: true }));
  }, []);

  const toggle = useCallback(
    (id: string) => {
      const willExpand = !expanded.has(id);
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      // Seed the first page on expand (separate setState — updaters stay pure).
      if (willExpand) {
        setPageById((p) => (p[id] ? p : { ...p, [id]: firstPage() }));
      }
    },
    [expanded],
  );

  const goToNextPage = useCallback(
    (id: string, endCursor: string | null) => {
      startLoading(id);
      setPageById((p) => {
        const cur = p[id] ?? firstPage();
        return {
          ...p,
          [id]: {
            ...cur,
            first: cur.pageSize,
            last: null,
            after: endCursor,
            before: null,
            pageIndex: cur.pageIndex + 1,
          },
        };
      });
    },
    [startLoading],
  );

  const goToPrevPage = useCallback(
    (id: string, startCursor: string | null) => {
      startLoading(id);
      setPageById((p) => {
        const cur = p[id] ?? firstPage();
        return {
          ...p,
          [id]: {
            ...cur,
            first: null,
            last: cur.pageSize,
            after: null,
            before: startCursor,
            pageIndex: Math.max(0, cur.pageIndex - 1),
          },
        };
      });
    },
    [startLoading],
  );

  const setPageSize = useCallback(
    (id: string, size: number) => {
      startLoading(id);
      setPageById((p) => ({ ...p, [id]: firstPage(size) }));
    },
    [startLoading],
  );

  const onLoaded = useCallback((id: string, data: TData) => {
    setDataById((prev) => (prev[id] === data ? prev : { ...prev, [id]: data }));
    setLoadingById((l) => (l[id] ? { ...l, [id]: false } : l));
  }, []);

  return {
    expanded,
    pageById,
    dataById,
    loadingById,
    toggle,
    goToNextPage,
    goToPrevPage,
    setPageSize,
    onLoaded,
  };
}
